/**
 * SeoKeywordMapPage — `/seo/keyword-map` (A1). Карта «кластер → страница» +
 * гэп-лист денежных кластеров без страницы. «Собрать ядро» — из seed-фраз
 * (детерминированный черновик; AI-обогащение — через чат со скиллом seo).
 */

import { useEffect, useRef, useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { SEO_HELP } from '@/help/seoHelp';
import { Button, LoadingBlock, ErrorBlock, Badge, Spinner } from '@/shared/ui';
import { useSeoClusters, useSeoGaps, useSeoMutations, type SeoCluster } from '@/hooks/api/useSeo';

function num(v: number): string {
  return new Intl.NumberFormat('ru-RU').format(v);
}

export function SeoKeywordMapPage() {
  usePageHeader({ title: 'SEO — Семантика и карта', subtitle: 'BOFU-first кластеры и гэп-лист', actions: <HelpButton help={SEO_HELP['keyword-map']} /> });
  const [tab, setTab] = useState<'all' | 'gaps'>('all');
  const [building, setBuilding] = useState(false);
  const clusters = useSeoClusters({}, building);
  const gaps = useSeoGaps(building);
  const { buildClusters, createTask } = useSeoMutations();
  const [seeds, setSeeds] = useState('');
  const baselineCount = useRef(0);

  const allClusters = clusters.data?.clusters ?? [];
  const rows = tab === 'gaps' ? (gaps.data?.gaps ?? []) : allClusters;
  const loading = tab === 'gaps' ? gaps.isLoading : clusters.isLoading;
  const err = tab === 'gaps' ? gaps.error : clusters.error;

  // Останавливаем поллинг, когда AI дособрал (кластеров стало больше) или по таймауту.
  useEffect(() => {
    if (building && allClusters.length > baselineCount.current) setBuilding(false);
  }, [building, allClusters.length]);

  const build = () => {
    const list = seeds.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    baselineCount.current = allClusters.length;
    buildClusters.mutate(
      { seeds: list },
      {
        onSuccess: (res) => {
          setSeeds('');
          if (res?.status === 'started') {
            setBuilding(true);
            setTimeout(() => setBuilding(false), 180_000); // страховочный стоп
          }
        },
      },
    );
  };

  const toTask = (c: SeoCluster) =>
    createTask.mutate({
      source_module: 'keyword-map',
      title: `Сделать страницу под кластер «${c.name}»`,
      description: `Гэп: кластер без страницы. Головная фраза: ${c.head_keyword || c.name}. Тип: ${c.page_type || '—'}.`,
      engine: c.engine,
      impact: c.priority >= 4 ? 'high' : 'med',
      effort: 'M',
      confidence: 'Likely',
      ref_cluster_id: c.id,
    });

  return (
    <div className="col gap20" style={{ maxWidth: 1200 }}>
      <section className="card">
        <h3 style={{ marginTop: 0 }}>Собрать ядро</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>
          Введите seed-фразы (по одной в строке) — создадим черновые BOFU-кластеры. Обогащение
          (спрос Wordstat, SERP-конкуренты, скор реалистичности) — через AI-чат со скиллом <span className="mono">seo</span>.
        </p>
        <textarea className="input" rows={3} placeholder={'корпоративный мессенджер\nальтернатива telegram для бизнеса'} value={seeds} onChange={(e) => setSeeds(e.target.value)} style={{ width: '100%', marginBottom: 8 }} disabled={building} />
        <Button variant="primary" size="sm" onClick={build} disabled={buildClusters.isPending || building || !seeds.trim()}>
          {buildClusters.isPending ? 'Запускаю…' : 'Собрать ядро (AI)'}
        </Button>
        {building && (
          <div className="row center" style={{ gap: 8, marginTop: 10 }}>
            <Spinner />
            <span style={{ fontSize: 13 }}>AI собирает ядро (обычно 1–2 мин) — кластеры появятся здесь автоматически.</span>
          </div>
        )}
        {buildClusters.isError && <ErrorBlock text={buildClusters.error?.message} />}
      </section>

      <div className="row" style={{ gap: 8 }}>
        <Button size="sm" variant={tab === 'all' ? 'primary' : 'outline'} onClick={() => setTab('all')}>Все кластеры</Button>
        <Button size="sm" variant={tab === 'gaps' ? 'primary' : 'outline'} onClick={() => setTab('gaps')}>
          Гэпы {gaps.data ? `(${gaps.data.gaps.length})` : ''}
        </Button>
      </div>

      {loading && <LoadingBlock label="Загружаю кластеры…" />}
      {err && <ErrorBlock text={err.message} />}

      {!loading && rows.length === 0 && (
        <div className="card"><p className="muted" style={{ margin: 0 }}>{tab === 'gaps' ? 'Гэпов нет — все кластеры закрыты страницами.' : 'Кластеров нет. Соберите ядро выше.'}</p></div>
      )}

      {rows.length > 0 && (
        <section className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>Кластер</th>
                <th>Этап</th>
                <th>ПС</th>
                <th style={{ textAlign: 'right' }}>Объём/мес</th>
                <th style={{ textAlign: 'right' }}>Приоритет</th>
                <th>Статус</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="col" style={{ gap: 2 }}>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{c.head_keyword || '—'}{c.page_type ? ` · ${c.page_type}` : ''}</span>
                    </div>
                  </td>
                  <td><Badge tone={c.funnel_stage === 'BOFU' ? 'b-success' : 'b-neutral'}>{c.funnel_stage}</Badge></td>
                  <td><span className="muted" style={{ fontSize: 12 }}>{c.engine}</span></td>
                  <td className="tnum" style={{ textAlign: 'right' }}>{num(c.volume)}</td>
                  <td className="tnum" style={{ textAlign: 'right' }}>{c.priority}</td>
                  <td><span className="muted" style={{ fontSize: 12 }}>{c.status}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!c.page_id && (
                      <Button size="sm" variant="outline" onClick={() => toTask(c)} disabled={createTask.isPending}>В задачи</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
